import { z } from 'zod';
import { EntityRef } from '../graph/entity-ref.js';
/**
 * Near-miss feed — the wire shape for
 * `GET /api/orgs/:o/memory/near-misses`.
 *
 * The "near-miss" surface is `/memory/near-misses`: every time the
 * agent caught a screw-up before it landed. Each row carries one or
 * more `NearMissImpact` rows that estimate the avoided cost (renewal
 * risk, hours of rework saved, days pulled forward). The impact
 * attribution is heuristic — the goal is to give the CEO a felt-sense
 * "what did the agent save me this month" number, not an actuarial
 * audit. Confidence pills surface the uncertainty.
 */
/**
 * Closed set of impact attribution kinds. Each impact row carries one
 * of these so the renderer can pick the right glyph/color:
 *   renewal-risk        — "$48,000 ARR at risk" — agent caught a churn signal
 *   rework-saved        — "3.5 hours of rework avoided" — caught before send
 *   time-pulled-forward — "31 days pulled forward" — flagged a deadline early
 *   revenue-saved       — "$12,000 saved" — direct revenue impact
 *   error-prevented     — generic fallback when no quantitative slot fits
 */
export const NearMissImpactKind = z.enum([
    'renewal-risk',
    'rework-saved',
    'time-pulled-forward',
    'revenue-saved',
    'error-prevented',
]);
/** One impact attribution row on a near-miss event. */
export const NearMissImpact = z.object({
    kind: NearMissImpactKind,
    /** Short human label — "ARR at risk", "Rework avoided", "Days pulled forward". */
    label: z.string().min(1).max(80),
    /** Display-formatted value with units — "$48,000", "3.5h", "31 days". */
    value: z.string().min(1).max(40),
    /**
     * Optional raw scalar — drives sorting + cross-row aggregation in the
     * tally strap. Same unit family as `value` (e.g. dollars for revenue,
     * hours for rework, days for time-pulled-forward).
     */
    numeric: z.number().optional(),
});
/** One near-miss event — a "catch" surfaced on the page grid + hero. */
export const NearMissEvent = z.object({
    id: z.string().min(1),
    /** ISO timestamp the catch was recorded. */
    caughtAt: z.string(),
    /** Eyebrow title — "Pulled the Acme renewal email back". */
    title: z.string().min(1).max(200),
    /** Body / sub copy. */
    body: z.string().max(800),
    /** Estimated impact rows — usually 1-3, drives the impact strap. */
    impact: z.array(NearMissImpact).default([]),
    /** Entities mentioned in this row. */
    linkedEntities: z.array(EntityRef).default([]),
    /** 0..1 confidence the catch is real (vs noisy). */
    confidence: z.number().min(0).max(1),
});
/** Footer tally — 4-stat strap at the bottom of the page. */
export const NearMissTally = z.object({
    totalSaves: z.number().int().min(0),
    /** Total hours estimated saved across the window — may be fractional. */
    hoursSaved: z.number().min(0),
    riskFlagged: z.number().int().min(0),
    correctionsLogged: z.number().int().min(0),
});
/** Wire shape returned by `GET /api/orgs/:o/memory/near-misses`. */
export const NearMissesResponse = z.object({
    /** Most recent + highest-impact catch — drives the hero card. */
    hero: NearMissEvent.optional(),
    /** Past 30 days, newest-first — drives the past-saves grid. */
    past30Days: z.array(NearMissEvent),
    tally: NearMissTally,
});
